import { SettingOutlined } from "@ant-design/icons";
import ChevronDown from "@untitled-ui/icons-react/build/esm/ChevronDown";
import {
  Card,
  Col,
  Collapse,
  Divider,
  Dropdown,
  Form,
  Row,
  Spin,
  Typography,
} from "antd";
import { connect } from "react-redux";
import { useLocation } from "react-router-dom";
import { AccountFacility } from "../../../components/facilities/TreasuryFacility";
import MainLayout from "../../../components/layouts/enhancement/MainLayout";
import TopContent from "../../../components/layouts/enhancement/TopContent";
import { labelConfig, staticConfig } from "../treasury.config";
import PreviewTable from "./components/PreviewTable";
import { Util } from "./helper/helper";
import {
  useGetProdTemplate,
  useGetTreasuryItem,
} from "./hooks/useServerActions";

const { Panel } = Collapse;
const { Text, Title } = Typography;

function ViewTreasuryBill(props) {
  const { userData } = props;
  const { session } = userData;
  const { state } = useLocation();
  const { prodTemplate } = useGetProdTemplate(session);
  const { loading, treasuryBillItem, menuProps } = useGetTreasuryItem(
    session,
    state?.key
  );

  const symbol = Util.getSymbol(prodTemplate, treasuryBillItem);

  const dataSections = [
    {
      header: "Product Information",
      items: [
        {
          name: "Product Code",
          labelValue: treasuryBillItem?.productCode,
          type: 1,
        },
        {
          name: "Product Name",
          labelValue: treasuryBillItem?.productName,
          type: 1,
        },
        {
          name: "Product Category",
          labelValue: treasuryBillItem?.productType.text ?? "----",
        },
        {
          name: "Currency",
          labelValue: treasuryBillItem?.currencyCode,
        },
        {
          name: "Branch Restriction",

          type: 3,
          items: treasuryBillItem?.restrictedOffice.map((item, i) => {
            return {
              labelValue: item.name,
            };
          }),
        },
        {
          name: "Accounting Classification",
          labelValue: treasuryBillItem?.accountingClassification?.name ?? "--",
        },
        {
          name: "Custodian",
          labelValue: treasuryBillItem?.custodian?.name ?? "----",
        },
        {
          name: "Start Date",
          labelValue: Util.formatFullDate(treasuryBillItem?.startDate),
        },
        {
          name: "End Date",
          labelValue: Util.formatFullDate(treasuryBillItem?.endDate),
        },
      ],
    },
    {
      header: "Instrument Parameters",
      items: [
        {
          name: "Instrument",
          labelValue: treasuryBillItem?.instrumentType?.name ?? "----",
        },
        {
          name: "Issuer",
          labelValue: treasuryBillItem?.issuer?.name ?? "----",
        },
        {
          name: "Security Type",
          labelValue: treasuryBillItem?.securityType?.text ?? "----",
        },
        {
          name: "Tenors Supported",
          labelValue: treasuryBillItem?.tenor?.text ?? "----",
        },
        {
          name: "Minimum Investment",
          labelValue: treasuryBillItem?.minInvestment ?? "----",
          prefix: symbol,
          type: 1,
        },
        {
          name: "Default Investment",
          labelValue: treasuryBillItem?.maxInvestment ?? "----",
          prefix: symbol,
          type: 1,
        },
        {
          name: "Investment Increment",
          labelValue: treasuryBillItem?.incrInvestment ?? "----",
          prefix: symbol,
          type: 1,
        },
        {
          name: "Interest Type",
          labelValue: treasuryBillItem?.interestType?.text ?? "----",
        },
        {
          name: "Redemption Mode",
          labelValue: treasuryBillItem?.redemptionMode.text ?? "----",
        },
        {
          name: "Allow Early Redemption",
          labelValue: treasuryBillItem?.redemptionMode.text,
          type: 2,
        },
      ],
    },
    {
      header: "Price and Rate Settings",
      items: [
        {
          name: "Pricing Method",
          labelValue: treasuryBillItem?.pricingMethod.text ?? "----",
        },
        {
          name: "Pricing Frequency",
          labelValue: treasuryBillItem?.pricingFrequency.text ?? "----",
        },
        {
          name: "Discount Rate",
          labelValue: treasuryBillItem?.discountRate,
          type: 1,
          suffix: "%",
        },
        {
          name: "Interest Rate Source",
          labelValue: treasuryBillItem?.interestRateSource?.name ?? "----",
        },
        {
          name: "Interest Rate Basis",
          labelValue: treasuryBillItem?.interestCalculationBasis?.text,
        },
        {
          name: "Accrual Method",
          labelValue: treasuryBillItem?.accrualMethod?.text,
        },
      ],
    },
    {
      header: "Maturity Settings",
      items: [
        {
          name: "Debit Source Account",
          labelValue: treasuryBillItem?.debitSourceAccountType?.text,
        },
        {
          name: "Settlement Date",
          labelValue: treasuryBillItem?.settlementDateType?.text,
        },
        {
          name: "Maturity Proceeds",
          labelValue: treasuryBillItem?.maturityProceed?.text ?? "----",
        },
        {
          name: "Rollover Option",
          labelValue: treasuryBillItem?.allowTax,
          type: 2,
        },
      ],
    },
    {
      header: "Tax & Charges",
      items: [
        {
          name: "Tax Group",
          type: 3,
          items: [
            {
              labelValue: treasuryBillItem?.allowTax ? "Yes" : "No",
            },
            treasuryBillItem?.allowTax
              ? {
                  labelValue: treasuryBillItem?.taxGroup?.name,
                }
              : null,
          ],
        },
        {
          name: "Setup Charges",
          type: 3,
          items: [
            {
              labelValue: treasuryBillItem?.setupCharges ? "Yes" : "No",
            },
            treasuryBillItem?.setupCharges
              ? {
                  labelValue: treasuryBillItem.setupCharge.name,
                }
              : null,
          ],
        },
        {
          name: "Redemption Charges",
          type: 3,
          items: [
            {
              labelValue: treasuryBillItem?.redemptionCharges ? "Yes" : "No",
            },
            treasuryBillItem?.redemptionCharges
              ? {
                  labelValue: treasuryBillItem.redemptionCharge.name,
                }
              : null,
          ],
        },
        {
          name: "Transfer Charges",
          type: 3,
          items: [
            {
              labelValue: treasuryBillItem?.transferCharges ? "Yes" : "No",
            },
            treasuryBillItem?.transferCharges
              ? {
                  labelValue: treasuryBillItem.transferCharge.name,
                }
              : null,
          ],
        },
        {
          name: "Brokerage/Agent Commission",
          type: 3,
          items: [
            {
              labelValue: treasuryBillItem?.allowBrokerage ? "Yes" : "No",
            },
            treasuryBillItem?.allowBrokerage
              ? {
                  labelValue: treasuryBillItem.commission.name,
                }
              : null,
          ],
        },
      ],
    },
    {
      header: "Accounting Settings",
      items: [
        {
          name: "Investment - Deal Booking",
          type: 3,
          items: [
            {
              labelValue:
                treasuryBillItem?.investmentGL?.debitAccount?.name ?? "----",
            },
            {
              labelValue:
                treasuryBillItem?.investmentGL?.creditAccount?.name ?? "----",
            },
          ],
        },
        {
          name: "Interest Accrual Posting",
          type: 3,
          items: [
            {
              labelValue:
                treasuryBillItem?.accruedInterestGL?.debitAccount?.name ??
                "----",
            },
            {
              labelValue:
                treasuryBillItem?.accruedInterestGL?.creditAccount?.name ??
                "----",
            },
          ],
        },

        {
          name: "Investment - Redemption (Customer Payable)",
          type: 3,
          items: [
            {
              labelValue:
                treasuryBillItem?.settlementGL?.debitAccount?.name ?? "----",
            },
            {
              labelValue:
                treasuryBillItem?.settlementGL?.creditAccount?.name ?? "----",
            },
          ],
        },
        {
          name: "Income Interest Posting",
          type: 3,
          items: [
            {
              labelValue:
                treasuryBillItem?.interestIncomeGL?.debitAccount?.name ??
                "----",
            },
            {
              labelValue:
                treasuryBillItem?.interestIncomeGL?.creditAccount?.name ??
                "----",
            },
          ],
        },

        {
          name: "Income Fees Posting",
          type: 3,
          items: [
            {
              labelValue:
                treasuryBillItem?.principalGL?.debitAccount?.name ?? "----",
            },
            {
              labelValue:
                treasuryBillItem?.principalGL?.creditAccount?.name ?? "----",
            },
          ],
        },
        {
          name: "Income Penalty Posting",
          type: 3,
          items: [
            {
              labelValue:
                treasuryBillItem?.penaltyGL?.debitAccount?.name ?? "----",
            },
            {
              labelValue:
                treasuryBillItem?.penaltyGL?.creditAccount?.name ?? "----",
            },
          ],
        },
      ],
    },
  ];

  const tabList = [
    {
      key: "transactions",
      label: "Transactions",
    },
  ];

  return (
    <MainLayout pageItems={staticConfig.ViewTreasuryBillBreadcrumbItems}>
      <TopContent
        pageTitle={labelConfig.treasuryBill}
        createPageTitle={"Treasury Bill Detail"}
      />
      <Spin spinning={loading} size="large">
        {state && (
          <>
            <Card
              headStyle={{ borderBottomWidth: 0 }}
              style={{
                boxShadow: "0px 0px 30px 3px rgba(0,0,0,0.1)",
                borderRadius: 20,
                marginTop: 20,
              }}
            >
              <Form
                // form={useForm}
                layout="vertical"
                style={{ paddingTop: 20, marginBottom: 50 }}
              >
                <Row>
                  <Title level={4} style={{ margin: 0, marginBottom: 0 }}>
                    {labelConfig.generalInfo}
                  </Title>
                </Row>
                <Divider style={{ marginTop: 10 }} />
                <Row>
                  <Col span={12} style={{ padding: "0px 8px" }}>
                    <Dropdown.Button
                      menu={menuProps}
                      icon={<SettingOutlined />}
                      placement="topLeft"
                    >
                      Actions
                    </Dropdown.Button>
                  </Col>

                  {/* <Col span={12} style={{ padding: "0px 8px" }}>
                    <ProductFacility
                      productName={treasuryBillItem?.productName}
                      productCode={treasuryBillItem?.productCode}
                      status={treasuryBillItem?.status.text}
                    />
                  </Col> */}
                </Row>
                <AccountFacility
                  status={treasuryBillItem?.status?.text}
                  name={treasuryBillItem?.productName}
                  id={treasuryBillItem?.productCode}
                  isProduct={true}
                />
                <Row
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 20,
                  }}
                >
                  {dataSections.map((item, i) => {
                    return (
                      <Col style={{}}>
                        <Collapse
                          className="custom-collapse"
                          // expandIconPosition="start"
                          expandIcon={<ChevronDown color="red" />}
                          style={{
                            backgroundColor: "transparent",
                            padding: 0,
                          }}
                        >
                          <Panel
                            header={
                              <span
                                style={{
                                  fontSize: "16px",
                                  fontWeight: 500,
                                }}
                              >
                                {item.header}
                              </span>
                            }
                            style={{
                              borderRadius: "8px",
                              overflow: "hidden",
                            }}
                            key={i}
                          >
                            <PreviewTable data={item.items} />
                          </Panel>
                        </Collapse>
                      </Col>
                    );
                  })}
                </Row>
              </Form>
            </Card>
          </>
        )}
        {!state && (
          <Card
            headStyle={{ borderBottomWidth: 0 }}
            style={{
              // boxShadow: "0px 0px 30px 3px rgba(0,0,0,0.1)",
              borderRadius: 20,
              marginTop: 20,
            }}
          >
            <Text>No data</Text>
          </Card>
        )}
      </Spin>
    </MainLayout>
  );
}

const mapStateToProps = (state) => ({
  userData: state.AuthenticationData.userData,
  rightBtn: state.RightSideBtn,
});

const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(ViewTreasuryBill);
